import React, { ReactNode, useCallback, useState, useContext, useEffect } from 'react';
import { Message } from '@scale/shared/types/chat-completion';
import { Button, Divider, TextArea, Typography, Icon } from '@scale/scaleui';
import cn from 'classnames';
import { useTrans } from './hooks/useTrans';
import {
  ReferenceText,
  Attachment,
  ChatMessageDisplayType,
  MessageRole,
  InteractionStepType,
  AutoFeedbackChatResponse,
} from '@scale/shared/types/chat';
import ReferenceTextGroup from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/ReferenceTextGroup';
import { useChatLayoutStore, ChatLayout } from './hooks/useChatLayoutStore';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { usePinStore } from './hooks/usePinStore';
import { AutoCritiqueButton, AutoCritiqueAlert, Alert, LaTeXEditor } from './TempComponents';
import { ResponseTemplatingProps } from './Card';
import { WorkerCommentProps } from 'react-scale/components/scalers/tasks/ThreadedInteraction/feedback/types';
import AttachmentGroup from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/AttachmentGroup';
import WordCounter from '../common/WordCounter';
import useAutoCritique from './hooks/useAutoCritique';
import UtilitiesWrapper from '../common/UtilitiesWrapper';
import {
  useChatStoreExtract,
  useInvalidErrorMessagesForTurn,
  useCurrentStep,
} from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import _ from 'lodash';
import { ChatMarkdown } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/ChatMarkdown';
import { useConfigureChatMessageDisplay } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useConfigureChatMessageDisplay';
import { twMerge } from 'tailwind-merge';
import { StepCard } from './step/StepCard';
import MessageRoleSelect from './components/MessageRoleSelect';
import { CommentButton } from './feedback/CommentButton';
import { ChatMessageDisplayConfigButton } from './step/ChatMessageDisplayConfig';
import { ResponseTemplatingButton } from './step/ResponseTemplatingButton';
import AutoFeedback from 'react-scale/components/scalers/tasks/ThreadedInteraction/AutoFeedback';
import useAutoFeedback from 'react-scale/components/scalers/tasks/ThreadedInteraction/AutoFeedback/hooks/useAutoFeedback';
import { AutoFeedbackProps } from 'react-scale/components/scalers/tasks/ThreadedInteraction/AutoFeedback/types';
import useMultimodalImageService from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useMultimodalImageService';
import useMountTime from 'react-scale/hooks/useMountTime';
import {
  Editor,
  EditorContainer,
  FooterContainer,
  PlainTextEditorContext,
  WordCounter as EditorWordCounter,
  KeyHandler,
} from './components/editor';
import useLatest from './hooks/useLatest';
import { useUser } from 'react-scale/components/ui/UserContext';

type Props = {
  className?: string;
  message: Message;
  placeholder?: ReactNode;
  onUpdateMessage?: (
    message: string,
    role: MessageRole,
    shouldResetHistory: boolean,
    editedReferenceTexts: ReferenceText[],
    editedAttachments: Attachment[],
    skipPromptInput: boolean,
    autoCritiqueResponse?: string,
    autoFeedbackResponse?: AutoFeedbackChatResponse,
  ) => Promise<boolean>;
  readOnly?: boolean;
  onClick?: () => void;
  referenceTexts?: ReferenceText[];
  enablePinning?: boolean;
  workerCommentProps?: WorkerCommentProps;
  responseTemplatingProps?: ResponseTemplatingProps;
  attachments?: Attachment[];
  turnIndex?: number; // treated as a 0-indexed representation of the turn number
  expandable?: boolean;
  expanded?: boolean;
  enableRTLText?: boolean;
  autoCritiqueResponse?: string;
  responseIndex?: number;
  autoFeedbackProps?: AutoFeedbackProps;
  hideErrors?: boolean;
  defaultDisplayType?: ChatMessageDisplayType;
};

// eslint-disable-next-line complexity
export function EditableChatMessage({
  className,
  message,
  placeholder,
  onUpdateMessage,
  readOnly: _readOnly,
  onClick,
  referenceTexts,
  enablePinning,
  workerCommentProps,
  responseTemplatingProps,
  attachments,
  turnIndex,
  expandable = false,
  expanded = false,
  enableRTLText,
  autoCritiqueResponse,
  responseIndex,
  autoFeedbackProps,
  hideErrors,
  defaultDisplayType,
}: Props) {
  const user = useUser();
  const isCorpUser: boolean = !!user?.isCorporateUser;

  const taskContext = useContext(ChatTaskContext);
  const multiChatIndex = useChatStoreExtract(state => state.currentMultiChatIndex);

  const config = useChatStoreExtract(state => state.config);
  const allowSelectPdfAttachmentAsReferenceText = config.allowSelectPdfAttachmentAsReferenceText;
  const referenceTextCategories = config.referenceTextCategories;
  const readOnly = _readOnly || config.disablePromptEditing;
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);
  const currentMultiChatIndex = useChatStoreExtract(state => state.currentMultiChatIndex);
  const step = useCurrentStep();
  const completedSteps = useChatStoreExtract(state => state.completedSteps);
  const complete = !_.isNil(responseIndex) ? completedSteps[responseIndex] : true;
  const editing = !readOnly && !complete;
  const markStepAsCompletedByIndex = useChatStoreExtract(state => state.markStepAsCompletedByIndex);
  const markStepAsIncompletedByIndex = useChatStoreExtract(
    state => state.markStepAsIncompletedByIndex,
  );

  const {
    minReferenceTexts,
    maxReferenceTexts,
    minReferenceTextsLength,
    maxReferenceTextsLength,
    hideReferenceURL,
    showReferenceCategory,
    minAttachments,
    maxAttachments,
    isReferenceTextReadOnly,
  } = config;
  /*
   * For this component, turnIndex > 0 means it's a multiturn step
   * This is because the turn number passed into this component is 0-indexed (i.e. turnIndex = 0 for the first turn)
   */

  const { t } = useTrans();

  const isMultiTurnStep = !!turnIndex;
  const enableAttachments =
    config.useAttachments && (!isMultiTurnStep || (config.useAttachmentsOnMultiTurn ?? true));
  const enableImageUrls =
    config.useImageUrls && (!isMultiTurnStep || config.useImageUrlsOnMultiTurn);
  const enableMmImageService =
    config.enableMmImageService && (!isMultiTurnStep || config.useImageUrlsOnMultiTurn);
  const isAttachmentRequired =
    config.isAttachmentRequired ||
    (config.isImageUrlRequired && !(isMultiTurnStep && config.requireImageUrlsOnlySingleTurn));
  // default useReferenceTextOnMultiTurn to true if not defined to maintain backwards compatibility
  const enableReferenceText =
    config.useReferenceText && (!isMultiTurnStep || (config.useReferenceTextOnMultiTurn ?? true));
  const isReferenceTextRequired =
    config.isReferenceTextRequired &&
    !(isMultiTurnStep && config.requireReferenceTextOnlySingleTurn);

  const {
    enableWordCounter,
    restrictRollbacks,
    disablePromptCopying: disableCopying,
    disablePromptPasting: disablePasting,
    forceHistoryReset,
  } = taskContext;
  const shouldForceHistoryReset = !!forceHistoryReset;

  const [shouldResetHistory, setShouldResetHistory] = useState<boolean>(false);
  const [editedText, setEditedText] = useState<string>(message.content);
  const [editedReferenceTexts, setEditedReferenceTexts] = useState<ReferenceText[]>(
    referenceTexts || [],
  );

  const [editedAttachments, setEditedAttachments] = useState<Attachment[]>(attachments || []);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [role, setRole] = useState<MessageRole>(message.role);
  // Function messages are also considered user prompts
  const isUserMessage = [MessageRole.User, MessageRole.Function].includes(message.role);

  const {
    chatMessageDisplayConfig,
    useMarkdownDisplay,
    updateMarkdownConfig,
    toggleChatMessageDisplayType,
  } = useConfigureChatMessageDisplay({
    displayType: defaultDisplayType,
  });

  const autoCritiqueHook = useAutoCritique(autoCritiqueResponse);
  const enableAutoCritique = taskContext.autoCritiqueConfig?.prompt?.enabled;
  const autoCritiqueLabel = taskContext.autoCritiqueConfig?.prompt?.renameButton;

  const startTime = useMountTime();
  const multimodalImageServiceHook = useMultimodalImageService({
    projectId: taskContext.projectId,
    assignmentId: taskContext.assignmentId,
    configVariables: config.templateVariables ?? {},
    reviewLevel,
    startTime,
  });

  const fetchAutoCritique = useCallback(
    async (editedText: string) => {
      const value = editedText;
      if (value) {
        await autoCritiqueHook.getAutoCritique({
          scope: 'prompt',
          projectId: taskContext.projectId,
          taskId: taskContext.taskId,
          assignmentId: taskContext.assignmentId,
          workerId: taskContext.workerId,
          variables: {
            ...config.templateVariables,
            prompt: value,
          },
        });
      }
    },
    [
      autoCritiqueHook,
      taskContext.projectId,
      taskContext.taskId,
      taskContext.assignmentId,
      taskContext.workerId,
      config.templateVariables,
    ],
  );

  const autoFeedbackHook = useAutoFeedback({
    id: autoFeedbackProps?.childId ?? '',
    responseIndex,
    reviewLevel,
    stepType: InteractionStepType.PromptInput,
    stepId: step?.id,
    multichatIndex: currentMultiChatIndex,
  });

  const shouldUseAutoFeedback =
    !taskContext.enableUnifiedFeedback &&
    !!autoFeedbackProps?.childId &&
    (autoFeedbackHook?.compatibleSteps.includes(InteractionStepType.PromptInput) ||
      autoFeedbackHook?.compatibleSteps.includes(step?.id ?? ''));

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setErrorMessage('');
    return setEditedText(e.target.value);
  };

  const validateReferenceText = useCallback(() => {
    setErrorMessage('');
    const hasRequiredReferenceTexts = !(
      isReferenceTextRequired &&
      enableReferenceText &&
      (!editedReferenceTexts || editedReferenceTexts.length === 0)
    );
    if (!hasRequiredReferenceTexts)
      setErrorMessage('Please add Reference Texts by pressing the "Add" icon.');
    const hasTooFewReferenceTexts =
      enableReferenceText &&
      isReferenceTextRequired &&
      !_.isNil(minReferenceTexts) &&
      editedReferenceTexts.length < minReferenceTexts;
    if (hasTooFewReferenceTexts)
      setErrorMessage(`Please add at least ${minReferenceTexts} reference texts.`);
    const hasTooManyReferenceTexts =
      enableReferenceText &&
      !_.isNil(maxReferenceTexts) &&
      editedReferenceTexts.length > maxReferenceTexts;
    if (hasTooManyReferenceTexts)
      setErrorMessage(`Please add at most ${maxReferenceTexts} reference texts.`);
    const hasInvalidReferenceTextLength = editedReferenceTexts.some(
      referenceText =>
        (referenceText.content?.length ?? 0) < (minReferenceTextsLength ?? 0) ||
        (referenceText.content?.length ?? 0) > (maxReferenceTextsLength ?? Infinity),
    );
    if (hasInvalidReferenceTextLength)
      setErrorMessage(
        `Reference text must be between ${minReferenceTextsLength} and ${maxReferenceTextsLength} characters long.`,
      );
    const isValid =
      hasRequiredReferenceTexts &&
      !hasTooFewReferenceTexts &&
      !hasTooManyReferenceTexts &&
      !hasInvalidReferenceTextLength;
    return isValid;
  }, [
    enableReferenceText,
    isReferenceTextRequired,
    editedReferenceTexts,
    minReferenceTexts,
    maxReferenceTexts,
    minReferenceTextsLength,
    maxReferenceTextsLength,
  ]);

  const validateAttachments = useCallback(() => {
    setErrorMessage('');
    const hasRequiredAttachments = !(
      isAttachmentRequired &&
      (enableAttachments || enableImageUrls || enableMmImageService) &&
      (!editedAttachments || editedAttachments.length === 0)
    );
    const attachmentsEnabledOnStep = enableAttachments || enableImageUrls;
    if (!hasRequiredAttachments)
      setErrorMessage('Please add Attachments by pressing the "File" icon.');
    const hasTooFewAttachments =
      attachmentsEnabledOnStep &&
      !_.isNil(minAttachments) &&
      editedAttachments.length < minAttachments;
    if (hasTooFewAttachments) setErrorMessage(`Please add at least ${minAttachments} attachments.`);
    const hasTooManyAttachments =
      attachmentsEnabledOnStep &&
      !_.isNil(maxAttachments) &&
      editedAttachments.length > maxAttachments;
    if (hasTooManyAttachments) setErrorMessage(`Please add at most ${maxAttachments} attachments.`);
    const isValid = hasRequiredAttachments && !hasTooFewAttachments && !hasTooManyAttachments;
    return isValid;
  }, [
    editedAttachments,
    enableAttachments,
    enableImageUrls,
    enableMmImageService,
    isAttachmentRequired,
    maxAttachments,
    minAttachments,
  ]);

  const onKeyDownSubmit = (e: React.KeyboardEvent) => {
    // Catch shift + enter to submit
    if (e.shiftKey && e.key === 'Enter') {
      e.preventDefault();
      handleEditSubmitRef.current();
    }
  };

  const handleEditSubmitHelper = useCallback(
    async ({ skipPromptInput }: { skipPromptInput: boolean } = { skipPromptInput: false }) => {
      if (!validateReferenceText()) return;
      if (!validateAttachments()) return;
      if (!onUpdateMessage) {
        return;
      }
      // Clear the text if we are skipping the prompt input
      const textToSubmit = skipPromptInput ? '' : editedText;
      const isValid = await onUpdateMessage(
        textToSubmit,
        role,
        shouldResetHistory,
        editedReferenceTexts,
        editedAttachments,
        skipPromptInput,
        autoCritiqueHook?.critiqueData?.critique,
        { feedback: autoFeedbackHook?.feedback ?? [] },
      );
      if (isValid && !_.isNil(responseIndex)) {
        markStepAsCompletedByIndex(responseIndex);
      }
    },
    [
      autoCritiqueHook?.critiqueData?.critique,
      autoFeedbackHook?.feedback,
      editedAttachments,
      editedReferenceTexts,
      editedText,
      markStepAsCompletedByIndex,
      onUpdateMessage,
      responseIndex,
      role,
      shouldResetHistory,
      validateAttachments,
      validateReferenceText,
    ],
  );

  const handleEditSubmitRef = useLatest(handleEditSubmitHelper);

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (disablePasting) {
      e.preventDefault();
      setErrorMessage('This field does not allow pasting.');
      return;
    }
  };

  const handleDragAndDrop = (e: React.DragEvent<HTMLTextAreaElement>) => {
    if (disablePasting) {
      e.preventDefault();
      setErrorMessage('This field does not allow dragging and dropping.');
      return;
    }
  };

  const isDrawerOpen = useChatLayoutStore(state => state.isDrawerOpen);
  const setIsDrawerOpen = useChatLayoutStore(state => state.setIsDrawerOpen);
  const layout = useChatLayoutStore(state => state.layout);

  const isPinned = usePinStore(state =>
    responseIndex ? state.isPinnedMessage(responseIndex, multiChatIndex) : false,
  );
  const isPinnable = usePinStore(state => enablePinning && state.isPinnable(responseIndex));
  const addPinnedMessage = usePinStore(state => state.addPinnedMessage);
  const removePinnedMessage = usePinStore(state => state.removePinnedMessage);

  // MultiChat: Pin the prompts across chats
  useEffect(() => {
    if (isPinned && responseIndex) {
      addPinnedMessage(responseIndex, message, multiChatIndex);
    }
  }, [isPinned, responseIndex, addPinnedMessage, message, multiChatIndex]);

  const invalidErrorMessages = useInvalidErrorMessagesForTurn(turnIndex);

  const {
    showCommentIcon,
    childId: workerCommentChildId,
    turnIndex: workerCommentTurnIndex,
  } = workerCommentProps || {
    showCommentIcon: false,
    childId: '',
    canEdit: false,
    areCommentsReadOnly: false,
    turnIndex: -1,
  };

  const { childId: responseTemplatingChildId, metadata } = responseTemplatingProps || {
    showResponseTemplatingIcon: false,
    childId: '',
    metadata: {},
  };

  const showPinAction = isPinnable && layout === ChatLayout.Inline;
  const showMessageRoleSelector = config.showMessageRoles && role && editing;
  const showSkipPromptInputButton = config.enableSkipPromptInput && isMultiTurnStep && editing;

  const enableMessageCopying = ((!disableCopying && !expanded) || isCorpUser) && !editing;
  const enablePromptEditing = !shouldForceHistoryReset && !editing && !readOnly && isUserMessage;
  const enablePromptRetry =
    (shouldForceHistoryReset || !restrictRollbacks) && !editing && !readOnly && isUserMessage;

  const textAreaComponent = (
    <PlainTextEditorContext
      initialContent={editedText}
      extensions={[
        KeyHandler.configure({
          shortcuts: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'Shift-Enter': () => {
              handleEditSubmitRef.current();
              return true;
            },
          },
        }),
      ]}
    >
      <EditorContainer className="w-full">
        <Editor
          value={editedText}
          onContentChange={value => setEditedText(value)}
          disablePaste={disablePasting}
          onDisabledPaste={() => setErrorMessage('This field does not allow pasting.')}
          data-testid="chat-message-editor"
        />
        <FooterContainer>
          <EditorWordCounter />
        </FooterContainer>
      </EditorContainer>
    </PlainTextEditorContext>
  );

  return (
    <>
      <StepCard
        className={twMerge(cn('focus:ring-0', className))}
        onClick={onClick}
        tabIndex={expandable ? -1 : 0}
        readOnly={readOnly}
      >
        <StepCard.Header>
          <StepCard.HeaderLeft>
            {isUserMessage ? (
              <StepCard.Icon icon="user" />
            ) : (
              <StepCard.Image src="https://static.remotasks.com/uploads/602b25a6e0984c00343d3b26/Frame.png" />
            )}
            {!_.isNil(turnIndex) && (
              <StepCard.HeaderColumn>
                <StepCard.Title className="inline">
                  Turn #{turnIndex! + 1}
                  {isUserMessage ? ' - Prompt' : ' - Response'}
                </StepCard.Title>
                {!isUserMessage && !hideErrors && invalidErrorMessages.length > 0 && (
                  <StepCard.Subtitle className="inline">
                    <div className="flex flex-row items-center">
                      <Icon icon={['far', 'exclamation-circle']} size="sm" />
                      {invalidErrorMessages[0]}
                    </div>
                  </StepCard.Subtitle>
                )}
              </StepCard.HeaderColumn>
            )}
          </StepCard.HeaderLeft>
          <StepCard.HeaderRight>
            {enableMessageCopying && (
              <StepCard.Button
                tooltip="Copy this chat message"
                icon="copy"
                onClick={e => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(message.content);
                }}
              />
            )}
            {enablePromptEditing && (
              <StepCard.Button
                tooltip={'Edit this chat message'}
                icon="edit"
                onClick={() => {
                  setShouldResetHistory(shouldForceHistoryReset);
                  setEditedText(message.content);
                  if (!_.isNil(responseIndex)) {
                    markStepAsIncompletedByIndex(responseIndex);
                  }
                }}
                data-testid="edit-prompt-button"
              />
            )}
            {enablePromptRetry && (
              <StepCard.Button
                icon="undo"
                tooltip="Retry chat from here"
                onClick={() => {
                  if (
                    !window.confirm(
                      'Performing this action will wipe the turn history after this point. Are you sure you want to continue?',
                    )
                  ) {
                    return;
                  }
                  setShouldResetHistory(true);
                  setEditedText(message.content);
                  if (!_.isNil(responseIndex)) {
                    markStepAsIncompletedByIndex(responseIndex);
                  }
                }}
              />
            )}

            {!taskContext.enableUnifiedFeedback && (
              <CommentButton
                childId={workerCommentChildId}
                visible={showCommentIcon}
                turnIndex={workerCommentTurnIndex}
              />
            )}
            <ResponseTemplatingButton childId={responseTemplatingChildId} metadata={metadata} />
            <StepCard.Button
              icon={isPinned ? 'slash' : 'thumbtack'}
              iconProps={{
                mask: isPinned ? 'thumbtack' : undefined,
                transform: isPinned ? 'down-1' : undefined,
              }}
              disabled={!showPinAction}
              tooltip={
                !showPinAction
                  ? 'Open the pinned item drawer to pin this step'
                  : isPinned
                  ? 'Unpin this prompt'
                  : 'Pin this prompt'
              }
              onClick={() => {
                if (!isPinnable) return;
                if (!isDrawerOpen) setIsDrawerOpen(true);
                if (isPinned) {
                  removePinnedMessage(responseIndex!, multiChatIndex);
                  return;
                }
                addPinnedMessage(responseIndex!, message, multiChatIndex);
              }}
              enableDisabledTooltip
            />
            {!expanded && (
              <ChatMessageDisplayConfigButton
                chatMessageDisplayConfig={chatMessageDisplayConfig}
                useMarkdownDisplay={useMarkdownDisplay}
                updateMarkdownConfig={updateMarkdownConfig}
                toggleChatMessageDisplayType={toggleChatMessageDisplayType}
              />
            )}
            {expandable && expanded && (
              <StepCard.Button tooltip="Hide step history" icon="chevron-up" />
            )}
            {expandable && !expanded && (
              <StepCard.Button tooltip="Expand and show step history" icon="chevron-down" />
            )}
          </StepCard.HeaderRight>
        </StepCard.Header>
        <StepCard.Body className={cn('flex flex-row w-full px-4 py-3 items-center')}>
          {editing ? (
            <>
              <div className="flex w-full flex-col gap-1 ml-2 flex-1">
                {showMessageRoleSelector && (
                  <div className="mb-4">
                    <MessageRoleSelect role={role} setRole={setRole} />
                  </div>
                )}
                <UtilitiesWrapper enableRTL={enableRTLText}>
                  {chatMessageDisplayConfig.markdownConfig.enableMathPlugin ? (
                    <LaTeXEditor
                      initialText={editedText || ''}
                      onChange={setEditedText}
                      readOnly={!!readOnly}
                      placeholder="Start typing here..."
                      enableSingleDollarTextMath={true}
                      direction="row"
                    >
                      <TextArea
                        id="text"
                        data-field-id={`turn${turnIndex ?? 0}message${
                          responseIndex ? `-${responseIndex}` : ''
                        }`}
                        value={editedText}
                        placeholder="Start typing here..."
                        onChange={handleInputChange}
                        onKeyDown={onKeyDownSubmit}
                        onPaste={handlePaste}
                        onDragOver={e => {
                          if (disablePasting) {
                            e.preventDefault();
                          }
                        }}
                        onDrop={handleDragAndDrop}
                        autoFocus
                        dynamic
                        className="flex-1 w-1/2"
                        data-testid="chat-message-textarea"
                      />
                    </LaTeXEditor>
                  ) : (
                    textAreaComponent
                  )}
                </UtilitiesWrapper>

                {enableWordCounter && chatMessageDisplayConfig.markdownConfig.enableMathPlugin && (
                  <div className="pt-2">
                    <WordCounter text={editedText} active={true} />
                  </div>
                )}
                {enableReferenceText && (
                  <ReferenceTextGroup
                    editing={editing}
                    referenceTexts={editedReferenceTexts}
                    required={isReferenceTextRequired}
                    hideUrl={hideReferenceURL}
                    showReferenceCategory={showReferenceCategory}
                    setReferenceTexts={setEditedReferenceTexts}
                    maxReferenceTexts={maxReferenceTexts}
                    allowSelectPdfAttachmentAsReferenceText={
                      allowSelectPdfAttachmentAsReferenceText
                    }
                    referenceTextCategories={referenceTextCategories}
                    readOnly={isReferenceTextReadOnly}
                    minReferenceTextsLength={minReferenceTextsLength}
                    maxReferenceTextsLength={maxReferenceTextsLength}
                  />
                )}
                {(enableAttachments || enableImageUrls || enableMmImageService) && (
                  <AttachmentGroup
                    attachments={editedAttachments}
                    setAttachments={setEditedAttachments}
                    readOnly={config.disableImageEditing}
                    enableImageUrls={enableImageUrls}
                    enableAttachments={enableAttachments}
                    enableMmImageService={enableMmImageService}
                    multimodalImageServiceHook={multimodalImageServiceHook}
                  />
                )}
                {shouldUseAutoFeedback && (
                  <div className="w-full mt-3">
                    <Divider />
                    <AutoFeedback
                      autoFeedbackHook={autoFeedbackHook}
                      inputContent={editedText}
                      readOnly={readOnly}
                      attachments={editedAttachments}
                    />
                  </div>
                )}
                {errorMessage && (
                  <Alert variant="warning" className="mt-2">
                    <Typography.Paragraph2>{errorMessage}</Typography.Paragraph2>
                  </Alert>
                )}
              </div>
              <div className="px-2"></div>
            </>
          ) : (
            <>
              <div className="flex ml-4 flex-col flex-1 w-full min-w-0">
                <div className="flex-1 w-full">
                  <div
                    className={cn('flex flex-col gap-1 w-full', {
                      'select-none': disableCopying,
                    })}
                  >
                    {message.content ? (
                      chatMessageDisplayConfig.displayType === ChatMessageDisplayType.Markdown ? (
                        <UtilitiesWrapper enableRTL={enableRTLText}>
                          <div className="w-full">
                            <ChatMarkdown
                              markdown={message.content}
                              enableMath={chatMessageDisplayConfig.markdownConfig.enableMathPlugin}
                              enableSingleDollarTextMath={
                                chatMessageDisplayConfig.markdownConfig.enableSingleDollarTextMath
                              }
                              enableHtml={taskContext.htmlMarkdownConfig?.enablePromptPreviews}
                              enableSoftBreaks={
                                taskContext.generalMarkdownConfig?.enableSoftLineBreaks
                              }
                              enableExecutableCodeBlocks={
                                taskContext.codingConfig?.enableExecutableCodeMarkdown ??
                                taskContext.generalMarkdownConfig?.enableExecutableCodeMarkdown
                              }
                              defaultExpandCodeBlocks={false}
                            />
                          </div>
                        </UtilitiesWrapper>
                      ) : (
                        <div className="text-neutral-900 text-sm whitespace-pre-wrap">
                          {message.content}
                        </div>
                      )
                    ) : (
                      placeholder
                    )}
                    {enableWordCounter && (
                      <div className="pt-2">
                        <WordCounter text={editedText} active={true} />
                      </div>
                    )}
                    {isUserMessage && (
                      <>
                        {enableAutoCritique && (
                          <AutoCritiqueAlert
                            autoCritiqueHook={autoCritiqueHook}
                            headerLabel={autoCritiqueLabel}
                          />
                        )}
                        {enableReferenceText && (
                          <ReferenceTextGroup
                            referenceTexts={editedReferenceTexts}
                            setReferenceTexts={setEditedReferenceTexts}
                            readOnly={readOnly || isReferenceTextReadOnly}
                            maxReferenceTexts={maxReferenceTexts}
                            hideUrl={hideReferenceURL}
                            showReferenceCategory={showReferenceCategory}
                            allowSelectPdfAttachmentAsReferenceText={
                              allowSelectPdfAttachmentAsReferenceText
                            }
                            minReferenceTextsLength={minReferenceTextsLength}
                            maxReferenceTextsLength={maxReferenceTextsLength}
                          />
                        )}
                        {(enableAttachments || enableImageUrls || enableMmImageService) && (
                          <AttachmentGroup
                            attachments={editedAttachments}
                            setAttachments={setEditedAttachments}
                            readOnly={true}
                            enableImageUrls={enableImageUrls}
                            enableAttachments={enableAttachments}
                            enableMmImageService={enableMmImageService}
                            multimodalImageServiceHook={multimodalImageServiceHook}
                          />
                        )}
                      </>
                    )}
                    {/* {config.showHtmlGen && message?.content && (
                      <ChatHtmlRenderer src={message.content} allowRegeneration={true} />
                    )} */}
                  </div>
                </div>
              </div>
            </>
          )}
        </StepCard.Body>
        {!editing && isUserMessage && (shouldUseAutoFeedback || enableAutoCritique) && (
          <StepCard.Footer className="flex flex-col">
            {enableAutoCritique && (
              <div className="flex justify-end w-full">
                <AutoCritiqueButton
                  onClick={() => fetchAutoCritique(editedText)}
                  loading={autoCritiqueHook.critiqueData.loading}
                  buttonLabel={autoCritiqueLabel}
                />
              </div>
            )}
            {shouldUseAutoFeedback && (
              <div className="w-full mt-3">
                <Divider />
                <AutoFeedback
                  autoFeedbackHook={autoFeedbackHook}
                  inputContent={editedText}
                  attachments={editedAttachments}
                  readOnly={true} // Unless the user is editing the prompt, AutoFeedback should be read-only
                />
              </div>
            )}
          </StepCard.Footer>
        )}
        {editing && isUserMessage && !readOnly && (
          <StepCard.Footer className="flex gap-3 justify-end">
            {showSkipPromptInputButton && (
              <Button
                variant="secondary"
                onClick={() => handleEditSubmitRef.current({ skipPromptInput: true })}
              >
                {t('skip-message')}
              </Button>
            )}
            <Button
              variant="neutral"
              onClick={() => {
                if (!_.isNil(responseIndex)) {
                  markStepAsCompletedByIndex(responseIndex);
                }
                setEditedReferenceTexts(referenceTexts || []);
                setEditedAttachments(attachments || []);
              }}
            >
              Cancel editing
            </Button>
            <Button
              data-testid="prompt-input-finish-editing-button"
              variant="success"
              onClick={() => handleEditSubmitRef.current()}
            >
              Finish editing
            </Button>
          </StepCard.Footer>
        )}
      </StepCard>
    </>
  );
}
